import { useId, useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatINR } from "../lib/format";

/** Account balance across a backtest, one step per realized trade.
 *
 *  Starts at the run's configured capital and moves by each closing trade's
 *  realized P&L, so the final point is capital + realized P&L. Entries are
 *  skipped — they move no money, and plotting them would just double up points
 *  on a flat segment. The break-even line sits at the starting capital: the
 *  curve is green above it (account up) and red below (account down).
 *
 *  Unrealized P&L is deliberately absent. This is the *booked* balance, which
 *  is why it sits flat while a position runs and steps when it closes.
 *
 *  Distinct from `EquityCurve`, which draws the dashboard's live P&L series
 *  from a prepared point list.
 */

interface TradeLike {
  ts: string;
  realized_pnl_delta: string;
}

interface Props {
  trades: TradeLike[];
  /** The run's `initial_capital` — the origin and the colour threshold. */
  initialCapital: number;
}

const UP = "#10b981"; // emerald-500
const DOWN = "#ef4444"; // red-500

/** Past this many points the chart costs more to paint than it reveals. */
const MAX_POINTS = 1200;

interface Pt {
  t: number;
  balance: number;
}

function parseTs(ts: string): number {
  if (!ts) return NaN;
  const hasTz = ts.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(ts);
  return new Date(hasTz ? ts : `${ts}Z`).getTime();
}

/** Thin to ~MAX_POINTS keeping each bucket's high AND low, so peaks and
 *  drawdown troughs survive — a plain stride would quietly flatten them. */
function downsample(pts: Pt[]): Pt[] {
  if (pts.length <= MAX_POINTS) return pts;
  const size = Math.ceil(pts.length / Math.floor(MAX_POINTS / 2));
  const out: Pt[] = [];
  for (let i = 0; i < pts.length; i += size) {
    const slice = pts.slice(i, i + size);
    let lo = slice[0];
    let hi = slice[0];
    for (const p of slice) {
      if (p.balance < lo.balance) lo = p;
      if (p.balance > hi.balance) hi = p;
    }
    const [a, b] = lo.t <= hi.t ? [lo, hi] : [hi, lo];
    out.push(a);
    if (b !== a) out.push(b);
  }
  const last = pts[pts.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

export default function BacktestBalanceCurve({ trades, initialCapital }: Props) {
  const gradientId = useId();

  const { points, min, max, final, rawCount } = useMemo(() => {
    let balance = initialCapital;
    const raw: Pt[] = [];
    for (const t of trades) {
      const delta = Number(t.realized_pnl_delta);
      if (!Number.isFinite(delta) || delta === 0) continue;
      const ms = parseTs(t.ts);
      if (!Number.isFinite(ms)) continue;
      balance += delta;
      raw.push({ t: ms, balance });
    }
    if (!raw.length) {
      return {
        points: [] as Pt[],
        min: initialCapital,
        max: initialCapital,
        final: initialCapital,
        rawCount: 0,
      };
    }
    // Anchor at the capital the run started with, just before the first trade.
    const withStart: Pt[] = [
      { t: raw[0].t - 1, balance: initialCapital },
      ...raw,
    ];
    let lo = withStart[0].balance;
    let hi = withStart[0].balance;
    for (const p of withStart) {
      if (p.balance < lo) lo = p.balance;
      if (p.balance > hi) hi = p.balance;
    }
    return {
      points: downsample(withStart),
      min: lo,
      max: hi,
      final: raw[raw.length - 1].balance,
      rawCount: withStart.length,
    };
  }, [trades, initialCapital]);

  if (!points.length) {
    return (
      <p className="text-sm text-muted">
        No realized trades in this run — nothing to plot.
      </p>
    );
  }

  // Split the stroke exactly at the starting capital. An SVG gradient maps to
  // the LINE's own bounding box (its min..max), not to the axis domain, so the
  // offset comes from the data extent even though the axis below is padded.
  const span = max - min;
  const cut = Math.min(1, Math.max(0, span > 0 ? (max - initialCapital) / span : 0));

  // Pad the axis so the break-even line is on screen even for a run that never
  // rose above — or never dipped below — its starting capital.
  const domLo = Math.min(min, initialCapital);
  const domHi = Math.max(max, initialCapital);
  const pad = (domHi - domLo) * 0.06 || Math.abs(initialCapital) * 0.01 || 1;

  const pnl = final - initialCapital;

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span className="text-muted">
          Start{" "}
          <span className="font-mono text-foreground">
            {formatINR(initialCapital)}
          </span>
        </span>
        <span className="text-muted">
          End <span className="font-mono text-foreground">{formatINR(final)}</span>
        </span>
        <span className="text-muted">
          Booked P&amp;L{" "}
          <span
            className={`font-mono ${pnl >= 0 ? "text-emerald-500" : "text-red-500"}`}
          >
            {pnl >= 0 ? "+" : ""}
            {formatINR(pnl)}
          </span>
        </span>
        <span className="text-muted">
          Peak <span className="font-mono text-foreground">{formatINR(max)}</span>
        </span>
        <span className="text-muted">
          Trough <span className="font-mono text-foreground">{formatINR(min)}</span>
        </span>
      </div>

      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={points}
            margin={{ top: 8, right: 16, bottom: 4, left: 8 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset={cut} stopColor={UP} />
                <stop offset={cut} stopColor={DOWN} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeOpacity={0.15} vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(v: number) =>
                new Date(v).toLocaleDateString(undefined, {
                  month: "short",
                  year: "2-digit",
                })
              }
              tick={{ fontSize: 11 }}
              minTickGap={40}
            />
            <YAxis
              domain={[domLo - pad, domHi + pad]}
              tickFormatter={(v: number) => formatINR(v)}
              tick={{ fontSize: 11 }}
              width={95}
            />
            <Tooltip
              contentStyle={{ fontSize: 12 }}
              labelFormatter={(v) => new Date(Number(v)).toLocaleString()}
              formatter={(v) => [formatINR(Number(v)), "Balance"]}
            />
            <ReferenceLine
              y={initialCapital}
              stroke="currentColor"
              strokeOpacity={0.45}
              strokeDasharray="4 4"
              label={{
                value: `Starting capital ${formatINR(initialCapital)}`,
                position: "insideTopLeft",
                fontSize: 11,
                fill: "currentColor",
                opacity: 0.7,
              }}
            />
            <Line
              type="linear"
              dataKey="balance"
              stroke={`url(#${gradientId})`}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="mt-2 text-xs text-muted">
        Booked balance only: it steps at each closing trade and stays flat while
        a position is open, so it will not match an intrabar mark. Green above
        the starting capital, red below.
        {points.length < rawCount
          ? " Thinned for display — peaks and troughs are preserved."
          : ""}
      </p>
    </div>
  );
}
